import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { useAuth } from '../context/AuthContext';
import { STATIC_PLANS } from '../data/staticPlans';
import { useBillingPage } from '../hooks/useBillingPage';
import { useHeldSize } from '../hooks/useHeldSize';
import { usePricingData } from '../hooks/usePricingData';
import { errorMessage } from '../services/apiError';
import { getDetectedCountry } from '../services/countryService';
import { restoreApplePurchasesForCurrentAccount } from '../services/paymentActions';
import type { PlanConfig } from '../services/subscriptionService';
import { MINIMUM_TOP_UP_CREDITS } from '../utils/creditGate';
import { formatCreditPrice, topupDisplayCurrency } from '../utils/creditPrice';
import { announceUpgradeDismissed } from '../utils/freeCreditsPrompts';
import { isCreditAllowanceHighlight } from '../utils/planCreditSummary';
import { isIOSApp } from '../utils/platformDetection';
import { resolvePricingCurrency } from '../utils/pricingCountry';
import { getPlanBillingInterval } from '../utils/pricingPlans';
import { isIndiaCountry } from '../utils/region';

import { CreditTopUpCard } from './billing/CreditTopUpCard';
import { PlanCreditBreakdown } from './PlanCreditBreakdown';
import { PlanTopupCallout } from './PlanTopupCallout';
import { useToast } from './Toast/Toast';

import './ChatBox/UpgradeModal.css';

interface UpgradeModalProps {
  onlyHigherPlans?: boolean;
  onClose: () => void;
  subtitle?: string;
}

interface BillingModalProps extends UpgradeModalProps {
  initialView?: 'plans' | 'credits';
  initialCreditAmount?: number;
}

const UPGRADE_PLANS = [
  {
    key: 'free',
    name: 'Trial',
    price: 'Free',
    period: '',
    description: 'Get started building with AI agents. No credit card required.',
    badge: null as string | null,
    featured: false,
    isCurrent: true,
    highlights: [
      '10 Credits on sign-up',
      'Top-up: $0.0146 / credit',
      '1 User',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
    ],
  },
  {
    key: 'standard',
    name: 'Standard',
    price: '$4.49',
    period: '/month',
    description: 'Affordable entry point with all AI agents included.',
    badge: 'Most Popular',
    featured: true,
    isCurrent: false,
    highlights: [
      '400 Credits / month',
      'Top-up: $0.0112 / credit',
      '+7 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
    ],
  },
  {
    key: 'standard_india',
    name: 'Standard',
    price: '$1.99',
    period: '/month',
    description: 'Affordable India entry point with all AI agents included.',
    badge: 'Most Popular',
    featured: true,
    isCurrent: false,
    highlights: [
      '200 Credits / month',
      'Top-up: $0.0112 / credit',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
    ],
  },
  {
    key: 'plus',
    name: 'Plus',
    price: '$25',
    period: '/month',
    description: 'For growing teams that need more power.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      '5,000 Credits / month (66% discount vs base)',
      'Top-up: $0.005 / credit (66% discount vs base)',
      '+86 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$250',
    period: '/month',
    description: 'For professional teams shipping to production.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      '50,000 Credits / month (66% discount vs base)',
      'Top-up: $0.005 / credit (66% discount vs base)',
      '+862 Bonus Credits every day',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
      'Custom Agent',
    ],
  },
  {
    key: 'unlimited',
    name: 'Unlimited',
    price: '$1,499',
    period: '/month',
    description: 'Unlimited included usage with no plan-credit wallet deductions.',
    badge: null,
    featured: false,
    isCurrent: false,
    highlights: [
      'Unlimited Credits included',
      'Unlimited Users (Free or Paid)',
      'Unlimited Projects',
      'All AI Agents included',
      'Stage & Prod K8s Deployments',
      'Auto Scaling (Horizontal)',
      'Custom Agent',
    ],
  },
];

type UpgradePlan = typeof UPGRADE_PLANS[number];
type BillingToast = { success: (msg: string) => void; error: (msg: string) => void };
type BillingCycle = 'monthly' | 'annual';
type BillingCurrency = 'USD' | 'INR';
const PLAN_RANK: Record<string, number> = {
  free: 0,
  trial: 0,
  standard: 1,
  standard_india: 1,
  byok: 1,
  plus: 2,
  pro: 3,
  unlimited: 4,
};

const BILLING_TOPUP_SUCCESS_EVENT = 'billing_topup_success';
const BILLING_UPGRADE_SUCCESS_EVENT = 'billing_upgrade_success';
const formatPlanMoney = (amount: number, currency: 'USD' | 'INR'): string => {
  const hasCents = !Number.isInteger(Number(amount.toFixed(2)));
  const formatted = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount);

  return `${currency === 'INR' ? '₹' : '$'}${formatted}`;
};
const formatAppleMoney = (amount: number): string => `$${amount.toFixed(2)}`;

const getPlanCycleAmounts = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
) => {
  const plan = pricingPlans[planKey] || STATIC_PLANS[planKey] || STATIC_PLANS.free;

  if (isIOSApp()) {
    return {
      monthly: plan.apple_price_monthly ?? plan.monthly_price,
      annual: plan.apple_price_annual ?? plan.annual_price ?? 0,
    };
  }

  if (currency === 'INR') {
    return {
      monthly: plan.monthly_price_inr ?? (plan.monthly_price * 80),
      annual: plan.annual_price_inr ?? ((plan.annual_price ?? 0) * 80),
    };
  }

  return { monthly: plan.monthly_price, annual: plan.annual_price ?? 0 };
};

const getPlanDisplayPrice = (
  planKey: string,
  currency: 'USD' | 'INR',
  billingCycle: BillingCycle = 'monthly',
  pricingPlans: Record<string, PlanConfig> = STATIC_PLANS,
): string => {
  if (planKey === 'free') return 'Free';

  const { monthly, annual } = getPlanCycleAmounts(planKey, currency, pricingPlans);
  const interval = getPlanBillingInterval(planKey, billingCycle === 'annual');
  const amount = interval === 'annual' ? annual / 12 : monthly;

  return isIOSApp() ? formatAppleMoney(amount) : formatPlanMoney(amount, currency);
};

/** The plan's live top-up rate in the shown currency, with the Free rate it is discounted from. */
const getPlanTopup = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): { rate: string; baseRate: string; discount: number } | null => {
  const rate = getPlanTopupRate(planKey, currency, pricingPlans);

  if (rate <= 0) return null;
  const baseRate = getPlanTopupRate('free', currency, pricingPlans);

  return {
    rate: `${formatCreditPrice(rate, topupDisplayCurrency(currency))} / credit`,
    baseRate: formatCreditPrice(baseRate, topupDisplayCurrency(currency)),
    discount: baseRate > 0 ? Math.round((1 - (rate / baseRate)) * 100) : 0,
  };
};

/** Each card lists only what its plan adds to the plan below it. */
const INCLUDED_PLAN: Record<string, { key: string; name: string }> = {
  standard: { key: 'free', name: 'Free' },
  standard_india: { key: 'free', name: 'Free' },
  plus: { key: 'standard', name: 'Standard' },
  pro: { key: 'plus', name: 'Plus' },
  unlimited: { key: 'pro', name: 'Pro' },
};

/** Feature rows: credit amounts and the top-up rate have their own rows on the card. */
const planFeatures = (plan: UpgradePlan): string[] => plan.highlights.filter((text) => (
  !isCreditAllowanceHighlight(text) && !text.startsWith('Top-up')
));

const getPlanFeatures = (plan: UpgradePlan): { includes: string | null; features: string[] } => {
  const included = INCLUDED_PLAN[plan.key];
  const lower = UPGRADE_PLANS.find((candidate) => candidate.key === included?.key);

  if (!included || !lower) return { includes: null, features: planFeatures(plan) };
  const inherited = new Set(planFeatures(lower));

  return { includes: included.name, features: planFeatures(plan).filter((text) => !inherited.has(text)) };
};

const getPlanRank = (tier: string): number => PLAN_RANK[tier.trim().toLowerCase()] ?? PLAN_RANK.free;

const getRegionalUpgradePlans = (userTier: string, userCountry?: string): UpgradePlan[] => {
  const currentStandardKey = userTier === 'standard' || userTier === 'standard_india' ? userTier : null;
  const standardKey = currentStandardKey || (isIndiaCountry(userCountry) && !isIOSApp() ? 'standard_india' : 'standard');

  return UPGRADE_PLANS.filter((plan) => (
    (plan.key !== 'standard' && plan.key !== 'standard_india') || plan.key === standardKey
  ));
};

const getVisibleUpgradePlans = (
  userTier: string,
  onlyHigherPlans: boolean,
  userCountry?: string,
): UpgradePlan[] => {
  const regionalPlans = getRegionalUpgradePlans(userTier, userCountry);

  if (!onlyHigherPlans) return regionalPlans;

  const currentRank = getPlanRank(userTier);

  return regionalPlans.filter((plan) => getPlanRank(plan.key) > currentRank);
};

/** The plan to spotlight among several: the one marked featured, else the next plan up from the user's. */
const getFeaturedPlanKey = (plans: UpgradePlan[], userTier: string): string | undefined => {
  if (plans.length < 2) return undefined;

  return (plans.find((plan) => plan.featured && plan.key !== userTier)
    ?? plans.find((plan) => getPlanRank(plan.key) > getPlanRank(userTier)))?.key;
};

type PlanBadge = { text: string; tone: 'current' | 'spotlight' | 'note' };

/** The current plan, the spotlighted plan, or a plan that cannot be billed yearly. */
const getPlanBadge = (plan: UpgradePlan, isCurrent: boolean, featured: boolean): PlanBadge | null => {
  if (isCurrent) return { text: 'Current Plan', tone: 'current' };
  if (featured) return { text: plan.badge ?? 'Recommended', tone: 'spotlight' };

  return plan.key !== 'free' && getPlanBillingInterval(plan.key, true) === 'monthly'
    ? { text: 'Monthly only', tone: 'note' }
    : null;
};

const PlanCheck: React.FC = () => (
  <svg className="upgrade-plan-check" viewBox="0 0 16 16" fill="none">
    <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlanCardCta: React.FC<{
  plan: UpgradePlan; userTier: string; featured: boolean; isLoading?: boolean; onSelect: () => void;
}> = ({ plan, userTier, featured, isLoading, onSelect }) => {
  if (plan.key === userTier) {
    return <button className="upgrade-plan-cta upgrade-plan-cta--current" disabled>Current Plan</button>;
  }

  if (plan.key === 'free') return null;

  return (
    <button
      className={`upgrade-plan-cta${featured ? ' upgrade-plan-cta--featured' : ''}${isLoading ? ' upgrade-plan-cta--loading' : ''}`}
      onClick={onSelect}
      disabled={isLoading}
      type="button"
    >
      {isLoading ? 'Processing...' : `Upgrade to ${plan.name}`}
    </button>
  );
};

const PlanCardHeading: React.FC<{ name: string; badge: PlanBadge | null }> = ({ name, badge }) => (
  <div className="upgrade-plan-heading">
    <div className="upgrade-plan-name">{name}</div>
    {badge && <span className={`upgrade-plan-badge upgrade-plan-badge--${badge.tone}`}>{badge.text}</span>}
  </div>
);

const PlanCardPrice: React.FC<{
  plan: UpgradePlan; currency: 'USD' | 'INR'; billingCycle: BillingCycle; pricingPlans: Record<string, PlanConfig>;
}> = ({ plan, currency, billingCycle, pricingPlans }) => {
  const billedAnnually = plan.key !== 'free' && getPlanBillingInterval(plan.key, billingCycle === 'annual') === 'annual';
  const annualTotal = getPlanCycleAmounts(plan.key, currency, pricingPlans).annual;
  const yearly = isIOSApp() ? formatAppleMoney(annualTotal) : formatPlanMoney(annualTotal, currency);

  return (
    <>
      <div className="upgrade-plan-price">
        <span className="upgrade-plan-amount">{getPlanDisplayPrice(plan.key, currency, billingCycle, pricingPlans)}</span>
        <span className="upgrade-plan-period">{billedAnnually ? '/month' : (plan.period || 'Forever free')}</span>
      </div>
      {plan.key !== 'free' && (
        <p className="upgrade-plan-billing-detail">{billedAnnually ? `${yearly} billed yearly` : 'Billed monthly'}</p>
      )}
    </>
  );
};

const PlanTopupRow: React.FC<{
  planKey: string; currency: 'USD' | 'INR'; pricingPlans: Record<string, PlanConfig>;
}> = ({ planKey, currency, pricingPlans }) => (
  <PlanTopupCallout topup={getPlanTopup(planKey, currency, pricingPlans)} />
);

const PlanFeatureList: React.FC<{ plan: UpgradePlan }> = ({ plan }) => {
  const { includes, features } = getPlanFeatures(plan);

  return (
    <div className="upgrade-plan-features">
      {includes && <p className="upgrade-plan-includes">Everything in {includes}{features.length > 0 ? ', plus' : ''}</p>}
      {features.length > 0 && (
        <ul className="upgrade-plan-highlights">
          {features.map((feature) => <li key={feature}><PlanCheck />{feature}</li>)}
        </ul>
      )}
    </div>
  );
};

const PlanCard: React.FC<{
  plan: UpgradePlan;
  index: number;
  featured: boolean;
  onSelect: () => void;
  isLoading?: boolean;
  userTier: string;
  currency: 'USD' | 'INR';
  billingCycle: BillingCycle;
  planConfig: PlanConfig;
  basePlanConfig: PlanConfig;
}> = ({ plan, index, featured, onSelect, isLoading, userTier, currency, billingCycle, planConfig, basePlanConfig }) => {
  const isCurrent = plan.key === userTier;
  const pricingPlans = { free: basePlanConfig, [plan.key]: planConfig };
  const badge = getPlanBadge(plan, isCurrent, featured);
  const billedAnnually = getPlanBillingInterval(plan.key, billingCycle === 'annual') === 'annual';

  return (
    <div
      className={`upgrade-plan-card${featured ? ' upgrade-plan-card--featured' : ''}${isCurrent ? ' upgrade-plan-card--current' : ''}`}
      style={{ animationDelay: `${0.08 + index * 0.08}s` }}
    >
      <div className="upgrade-plan-info">
        <PlanCardHeading name={plan.name} badge={badge} />
        <PlanCardPrice plan={plan} currency={currency} billingCycle={billingCycle} pricingPlans={pricingPlans} />
        <p className="upgrade-plan-desc">{plan.description}</p>
        <PlanCreditBreakdown planKey={plan.key} plan={planConfig} isAnnual={billedAnnually} />
        <PlanTopupRow planKey={plan.key} currency={currency} pricingPlans={pricingPlans} />
        <PlanFeatureList plan={plan} />
      </div>
      <PlanCardCta plan={plan} userTier={userTier} featured={featured} isLoading={isLoading} onSelect={onSelect} />
    </div>
  );
};

const getPlanTopupRate = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): number => {
  const plan = pricingPlans[planKey] || STATIC_PLANS[planKey] || STATIC_PLANS.free;

  if (plan.topups_allowed === false) return 0;
  if (isIOSApp()) return plan.topup_price_per_credit_apple ?? plan.topup_price_per_credit ?? 0;

  return currency === 'INR'
    ? (plan.topup_price_per_credit_inr ?? plan.topup_price_per_credit ?? 0)
    : (plan.topup_price_per_credit ?? 0);
};

const getAnnualDiscountPercent = (
  planKey: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): number => {
  const { monthly, annual } = getPlanCycleAmounts(planKey, currency, pricingPlans);

  if (monthly <= 0 || annual <= 0) return 0;

  return Math.max(0, Math.round((1 - (annual / (monthly * 12))) * 100));
};

type DiscountedUpgradePlan = {
  plan: UpgradePlan;
  savingsPercent: number;
};

const getDiscountedUpgradePlans = (
  plans: UpgradePlan[],
  userTier: string,
  currency: 'USD' | 'INR',
  pricingPlans: Record<string, PlanConfig>,
): DiscountedUpgradePlan[] => {
  const currentRate = getPlanTopupRate(userTier, currency, pricingPlans);

  if (currentRate <= 0) return [];

  return plans
    .filter((plan) => getPlanTopupRate(plan.key, currency, pricingPlans) > 0)
    .map((plan) => ({
      plan,
      savingsPercent: Math.round((1 - (getPlanTopupRate(plan.key, currency, pricingPlans) / currentRate)) * 100),
    }))
    .filter(({ savingsPercent }) => savingsPercent > 0)
    .slice(0, 2);
};

const SubscriptionSavingsOffer: React.FC<{
  plans: DiscountedUpgradePlan[];
  onViewPlans: () => void;
}> = ({ plans, onViewPlans }) => {
  if (plans.length === 0) return null;

  // The live saving on top-ups against the rate the user pays now, in the shown currency.
  const topupSavings = Math.max(...plans.map(({ savingsPercent }) => savingsPercent));

  return (
    <section className="billing-subscribe-offer" aria-labelledby="billing-subscribe-offer-title">
      <div className="billing-subscribe-offer__copy">
        <span className="billing-subscribe-offer__eyebrow">
          Up to {topupSavings}% off top-ups
        </span>
        <div>
          <h4 id="billing-subscribe-offer-title">Get monthly credits and cheaper top-ups</h4>
          <p>Includes lower top-up rates and annual billing savings.</p>
        </div>
      </div>
      <button className="billing-subscribe-offer__compare" onClick={onViewPlans} type="button">
        View plans <span aria-hidden="true">→</span>
      </button>
    </section>
  );
};

const loadUpgradeModules = () => import('../services/paymentActions');

const dispatchBillingSuccessEvent = (eventName: string) => {
  window.dispatchEvent(new CustomEvent(eventName));
};

const handlePaymentSuccess = async (
  refreshSubscription: (cb: () => void) => Promise<void>,
  refreshUser: () => Promise<void>,
  toast: { success: (msg: string) => void; error: (msg: string) => void }, onClose: () => void, msg: string,
) => {
  await refreshSubscription(() => { });
  await refreshUser();
  dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
  toast.success(msg);
  onClose();
};

const executeUpgrade = async (
  tier: string,
  isAnnual: boolean,
  currency: BillingCurrency,
  user: { email?: string; country?: string; id?: string; subscription_tier?: string } | null,
  refreshUser: () => Promise<void>,
  toast: { success: (msg: string) => void; error: (msg: string) => void }, onClose: () => void,
) => {
  if (isIOSApp()) {
    const [{ STATIC_PLANS }, { initiateAppleSubscription, refreshSubscription }] = await Promise.all([
      import('../data/staticPlans'),
      import('../services/paymentActions'),
    ]);
    const plan = STATIC_PLANS[tier];
    const productId = isAnnual ? plan?.apple_product_id_annual : plan?.apple_product_id_monthly;

    if (!productId) {
      toast.error('Apple subscription product not configured for this plan.');

      return;
    }

    await initiateAppleSubscription(
      productId,
      async (upgradedTier: string) => {
        try {
          await refreshSubscription(() => { });
          await refreshUser();
          dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
          toast.success(`Successfully upgraded to ${plan?.name || upgradedTier}!`);
          onClose();
        } catch {
          toast.success('Your upgrade was successful. Please refresh the page to see your updated plan.');
          onClose();
        }
      },
      (error: string) => toast.error(error),
      user?.id,
    );

    return;
  }

  const { initiateUpgrade, refreshSubscription, verifyTransaction } = await loadUpgradeModules();

  await initiateUpgrade(tier, isAnnual, user?.email, async (response, msg) => {
    try {
      await verifyTransaction(response);
      await handlePaymentSuccess(
        refreshSubscription, refreshUser, toast, onClose, msg,
      );
    } catch {
      toast.error('Your upgrade was successful. Please refresh the page to see your updated plan.');
    }
  }, currency);
};

const applyRestoreResult = async (
  result: Awaited<ReturnType<typeof restoreApplePurchasesForCurrentAccount>>,
  refreshUser: () => Promise<void>,
  toast: BillingToast,
  onClose: () => void,
) => {
  if (result.status === 'none') {
    toast.error('No active Apple subscription was found to restore for this Apple ID.');

    return;
  }

  await refreshUser();
  dispatchBillingSuccessEvent(BILLING_UPGRADE_SUCCESS_EVENT);
  toast.success(`Restored ${result.tier ? `${result.tier} plan` : 'your Apple subscription'}.`);
  onClose();
};

const executeRestorePurchases = async (
  refreshUser: () => Promise<void>,
  toast: BillingToast,
  onClose: () => void,
) => {
  const result = await restoreApplePurchasesForCurrentAccount();

  await applyRestoreResult(result, refreshUser, toast, onClose);
};

const useLazyUpgrade = (onClose: () => void, currency: BillingCurrency) => {
  const [upgradingTier, setUpgradingTier] = useState<string | null>(null);
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const { user, getCurrentUser } = useAuth();
  const toast = useToast();

  const handleUpgrade = useCallback(async (tier: string, isAnnual = false) => {
    setUpgradingTier(tier);

    try {
      await executeUpgrade(tier, isAnnual, currency, user, getCurrentUser, toast, onClose);
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'We couldn’t start the upgrade. Check the selected plan and try again.'));
    } finally {
      setUpgradingTier(null);
    }
  }, [currency, getCurrentUser, user, toast, onClose]);

  const handleRestorePurchases = useCallback(async () => {
    if (!isIOSApp()) return;

    setRestoringPurchases(true);
    try {
      await executeRestorePurchases(getCurrentUser, toast, onClose);
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'We couldn’t restore the Apple purchase. Confirm the Apple ID and try again.'));
    } finally {
      setRestoringPurchases(false);
    }
  }, [getCurrentUser, toast, onClose]);

  return { upgradingTier, restoringPurchases, handleUpgrade, handleRestorePurchases };
};

const getModalFocusTargets = (modal: HTMLDivElement): HTMLElement[] => (
  Array.from(modal.querySelectorAll<HTMLElement>([
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(','))).filter((element) => element.getAttribute('aria-hidden') !== 'true')
);

const useBillingModalKeyboard = (
  modalRef: React.RefObject<HTMLDivElement | null>,
  onClose: () => void,
) => {
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => {
      const modal = modalRef.current;
      const preferredTarget = modal?.querySelector<HTMLElement>('.credit-input')
        || modal?.querySelector<HTMLElement>('.upgrade-modal-close');

      preferredTarget?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();

        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const targets = getModalFocusTargets(modalRef.current);

      if (targets.length === 0) return;

      const firstTarget = targets[0];
      const lastTarget = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === firstTarget) {
        event.preventDefault();
        lastTarget.focus();
      } else if (!event.shiftKey && document.activeElement === lastTarget) {
        event.preventDefault();
        firstTarget.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [modalRef, onClose]);
};

const BillingModalInner = ({
  onlyHigherPlans = true,
  onClose,
  subtitle,
  initialView = 'plans',
  initialCreditAmount,
}: BillingModalProps): React.ReactElement => {
  const { user } = useAuth();
  const userTier = (user?.subscription_tier || 'free').toLowerCase();
  const subscriptionStatus = (user?.subscription_status || 'active').toLowerCase();
  const isUnlimited = userTier === 'unlimited' && ['active', 'cancel_pending'].includes(subscriptionStatus);
  const billingPageState = useBillingPage();
  const { plans: pricingPlans } = usePricingData();
  const modalRef = useRef<HTMLDivElement>(null);
  const preselectedCredits = Math.max(
    MINIMUM_TOP_UP_CREDITS,
    Math.ceil(initialCreditAmount ?? 0),
  );
  const [activeView, setActiveView] = useState<'plans' | 'credits'>(
    isUnlimited && initialView === 'credits' ? 'plans' : initialView,
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const billingCountry = user?.country || detectedCountry;
  // The profile page is the one place to choose a currency; otherwise it follows the country.
  const displayCurrency = resolvePricingCurrency(user?.preferred_currency, billingCountry, isIOSApp());
  const {
    upgradingTier,
    restoringPurchases,
    handleUpgrade,
    handleRestorePurchases,
  } = useLazyUpgrade(onClose, displayCurrency);

  // Closed without buying (✕, backdrop, Escape): free credits are offered next, every time.
  const dismiss = useCallback(() => {
    onClose();
    announceUpgradeDismissed();
  }, [onClose]);

  useBillingModalKeyboard(modalRef, dismiss);

  useEffect(() => {
    setActiveView(isUnlimited && initialView === 'credits' ? 'plans' : initialView);
  }, [initialView, isUnlimited]);

  useEffect(() => {
    if (user?.country) {
      setDetectedCountry(null);

      return;
    }

    let cancelled = false;

    getDetectedCountry()
      .then((country) => {
        if (!cancelled) setDetectedCountry(country);
      })
      .catch(() => {
        if (!cancelled) setDetectedCountry(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.country]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const closeOnTopUpSuccess = () => onClose();

    window.addEventListener(BILLING_TOPUP_SUCCESS_EVENT, closeOnTopUpSuccess);

    return () => window.removeEventListener(BILLING_TOPUP_SUCCESS_EVENT, closeOnTopUpSuccess);
  }, [onClose]);

  const visiblePlans = useMemo(() => {
    const plans = getVisibleUpgradePlans(userTier, onlyHigherPlans, billingCountry || undefined);

    return isIOSApp()
      ? plans.filter((plan) => plan.key !== 'pro' && plan.key !== 'unlimited')
      : plans;
  }, [billingCountry, onlyHigherPlans, userTier]);
  const annualDiscount = useMemo(() => Math.max(0, ...visiblePlans.map((plan) => (
    getAnnualDiscountPercent(plan.key, displayCurrency, STATIC_PLANS)
  ))), [displayCurrency, visiblePlans]);
  const cyclePlans = billingCycle === 'annual'
    ? visiblePlans.filter((plan) => getPlanBillingInterval(plan.key, true) === 'annual')
    : visiblePlans;
  const featuredKey = getFeaturedPlanKey(cyclePlans, userTier);
  // Switching between Add Credits and Plans keeps the dialog at the size it has reached.
  const heldSize = useHeldSize(modalRef);
  const switchView = (nextView: 'plans' | 'credits') => {
    heldSize.hold();
    setActiveView(nextView);
  };
  const switchCycle = (nextCycle: BillingCycle) => {
    heldSize.hold();
    setBillingCycle(nextCycle);
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (isUnlimited) return;

    event.preventDefault();
    const nextView = activeView === 'credits' ? 'plans' : 'credits';

    switchView(nextView);
    window.requestAnimationFrame(() => document.getElementById(`billing-${nextView}-tab`)?.focus());
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) dismiss();
  };

  const stopModalEventPropagation = (event: React.SyntheticEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return ReactDOM.createPortal(
    <div className="upgrade-modal-overlay" onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={`upgrade-modal upgrade-modal--${activeView}${heldSize.held ? ' upgrade-modal--held' : ''}`}
        style={heldSize.style}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        onPointerDown={stopModalEventPropagation}
        onMouseDown={stopModalEventPropagation}
        onTouchStart={stopModalEventPropagation}
        onClick={stopModalEventPropagation}
      >
        <button className="upgrade-modal-close" onClick={dismiss} type="button" aria-label="Close billing options">✕</button>
        <div className="upgrade-modal-header">
          <span className="upgrade-modal-eyebrow">Billing</span>
          <h3 id="upgrade-modal-title">
            {/* Must match the amount the form actually preselects, or the
                heading promises a purchase the field does not offer. */}
            {activeView === 'credits' && initialCreditAmount
              ? `Add ${preselectedCredits} credits to continue`
              : (activeView === 'credits' ? 'Add credits and keep building' : 'Choose a plan')}
          </h3>
          <p>
            {activeView === 'credits'
              ? (initialCreditAmount
                ? 'Enough for the selected work. Your project stays saved while you top up.'
                : 'Choose how many credits to add. Your work stays exactly where it stopped.')
              : (isUnlimited
                ? 'Your retained wallet stays unchanged while Unlimited usage is active.'
                : 'Get monthly credits and a lower rate for additional top-ups.')}
          </p>
          {subtitle && <p className="upgrade-modal-subtitle">{subtitle}</p>}
        </div>

        {activeView === 'plans' ? (
          <div id="billing-plans-panel" role="tabpanel" aria-labelledby="billing-plans-tab">
            <div className="billing-plan-controls">
              <div className="billing-cycle-selector">
                <span>Billing cycle</span>
                <div role="group" aria-label="Subscription billing cycle">
                  <button
                    className={billingCycle === 'monthly' ? 'active' : ''}
                    onClick={() => switchCycle('monthly')}
                    aria-pressed={billingCycle === 'monthly'}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={billingCycle === 'annual' ? 'active' : ''}
                    onClick={() => switchCycle('annual')}
                    aria-pressed={billingCycle === 'annual'}
                    type="button"
                  >
                    Annual {annualDiscount > 0 && <span>Save {annualDiscount}%</span>}
                  </button>
                </div>
              </div>
            </div>
            <div className={`upgrade-plans-grid upgrade-plans-grid--count-${cyclePlans.length}`}>
              {cyclePlans.map((plan, i) => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  index={i}
                  featured={plan.key === featuredKey}
                  onSelect={() => handleUpgrade(plan.key, billingCycle === 'annual')}
                  isLoading={upgradingTier === plan.key}
                  userTier={userTier}
                  currency={displayCurrency}
                  billingCycle={billingCycle}
                  planConfig={{ ...STATIC_PLANS[plan.key], ...pricingPlans[plan.key] }}
                  basePlanConfig={{ ...STATIC_PLANS.free, ...pricingPlans.free }}
                />
              ))}
            </div>
            {cyclePlans.length === 0 && (
              <div className="upgrade-modal-empty">
                {visiblePlans.length > 0
                  ? 'No annual upgrades are available. Select Monthly to see available plans.'
                  : 'You are already on the highest available plan.'}
              </div>
            )}
            {isIOSApp() && (
              <div className="upgrade-restore-panel">
                <button
                  className="upgrade-restore-button"
                  onClick={handleRestorePurchases}
                  disabled={restoringPurchases}
                  aria-busy={restoringPurchases}
                  type="button"
                >
                  {restoringPurchases && <span className="restore-button-spinner" aria-hidden="true" />}
                  <span>{restoringPurchases ? 'Restoring...' : 'Restore Plan Purchase'}</span>
                </button>
                <p>
                  Restores active Apple plan subscriptions only. Credit top-ups are consumable and are not restored.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div
            className="upgrade-modal-credit-panel"
            id="billing-credits-panel"
            role="tabpanel"
            aria-labelledby="billing-credits-tab"
          >
            <CreditTopUpCard
              onTopUp={billingPageState.handleTopUp}
              isProcessing={billingPageState.isProcessing}
              onAppleTopUp={billingPageState.onAppleTopUp}
              initialCreditAmount={initialCreditAmount}
              fallbackPlanId={userTier}
              currency={displayCurrency}
              layout={heldSize.held ? 'split' : 'stacked'}
              secondaryOffer={({ plans, currentPlanId, currency }) => {
                const discountedPlans = getDiscountedUpgradePlans(
                  visiblePlans,
                  currentPlanId || userTier,
                  currency,
                  plans,
                );

                return (
                  <SubscriptionSavingsOffer
                    plans={discountedPlans}
                    onViewPlans={() => {
                      setBillingCycle('annual');
                      switchView('plans');
                    }}
                  />
                );
              }}
            />
          </div>
        )}
        <div className="upgrade-modal-footer">
          <div className="upgrade-modal-tabs" role="tablist" aria-label="Billing options">
            {!isUnlimited && (
              <button
                id="billing-credits-tab"
                className={`upgrade-modal-tab${activeView === 'credits' ? ' upgrade-modal-tab--active' : ''}`}
                onClick={() => switchView('credits')}
                onKeyDown={handleTabKeyDown}
                role="tab"
                aria-selected={activeView === 'credits'}
                aria-controls="billing-credits-panel"
                type="button"
              >
                Add Credits
              </button>
            )}
            <button
              id="billing-plans-tab"
              className={`upgrade-modal-tab${activeView === 'plans' ? ' upgrade-modal-tab--active' : ''}`}
              onClick={() => switchView('plans')}
              onKeyDown={handleTabKeyDown}
              role="tab"
              aria-selected={activeView === 'plans'}
              aria-controls="billing-plans-panel"
              type="button"
            >
              Plans &amp; savings
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export function BillingModal(props: BillingModalProps): React.ReactElement {
  return <BillingModalInner {...props} />;
}

export function UpgradeModal(props: UpgradeModalProps): React.ReactElement {
  return <BillingModalInner {...props} initialView="plans" />;
}

export { BILLING_TOPUP_SUCCESS_EVENT, BILLING_UPGRADE_SUCCESS_EVENT };
